/**
 * A plain decimal money string: optional sign, digits, optional single decimal point. No
 * exponents, no `0x`/`0b`/`0o` literals, no `Infinity`, no whitespace inside.
 */
const MONEY_DECIMAL_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

/** What `formatOrgMoney` appends when the organisation has never chosen a currency. */
export const CURRENCY_NOT_SET_SUFFIX = '(currency not set)';

/**
 * Money whose currency comes from a NULLABLE organisation setting (SKU-1865 F2).
 *
 * `Organization.currency` is nullable, so some amounts have no currency to render them in.
 * A missing currency is stated, never guessed: falling back to USD (or to `$`) would assert
 * a currency the organisation did not choose, and `formatMoney`'s own catch path already
 * silently renders an unknown code as a bare number (CRITICAL RULE 2).
 *
 * A bare `''` amount stays `''` — "no amount" is the caller's own state to render, and this
 * function must not turn it into a zero.
 */
export function formatOrgMoney(
  amount: string | number | null | undefined,
  currencyCode: string | null | undefined,
  /**
   * Whether the organisation's currency setting has actually been READ yet.
   *
   * Three states, not two: a currency that is set, one the organisation explicitly has not
   * set, and one nobody has looked up yet because the settings query is still in flight or
   * failed. Collapsing the third into the second made the tab flash "(currency not set)" at an
   * organisation that has one — a false statement about their data, which is the same class of
   * error as the wrong currency (CodeRabbit).
   */
  currencyResolved = true,
): string {
  if (amount == null || amount === '') return '';

  // Strings and numbers are validated differently ON PURPOSE (CodeRabbit).
  //
  // For a STRING, the whole thing has to be a DECIMAL number. `Number.parseFloat('7.20oops')`
  // is 7.2, so a cost with trailing junk would be displayed as a clean figure; and plain
  // `Number` is not the fix either, because it accepts the other numeric literal forms —
  // `Number('0x10')` is 16, and `'0b11'`, `'0o7'`, `'Infinity'` and `'1e3'` all parse. None of
  // those is a money string any part of this app writes, so each one arriving means the value
  // is corrupt and must not be rendered.
  //
  // For a NUMBER there is nothing to parse and the syntax check is a category error: it would
  // test how JS happens to RENDER the value, and `String()` switches to exponent notation
  // below 1e-6 and at/above 1e21 — so `formatOrgMoney(1e-7, 'USD')` returned '' for a
  // perfectly finite amount the caller handed us. The only question for a number is whether it
  // is finite.
  let numeric: number;
  if (typeof amount === 'number') {
    if (!Number.isFinite(amount)) return '';
    numeric = amount;
  } else {
    const trimmed = amount.trim();
    if (!MONEY_DECIMAL_RE.test(trimmed)) return '';
    numeric = Number(trimmed);
    if (!Number.isFinite(numeric)) return '';
  }
  if (!currencyResolved) {
    // No claim either way until the setting is known.
    return numeric.toFixed(2);
  }
  if (!currencyCode) {
    return `${numeric.toFixed(2)} ${CURRENCY_NOT_SET_SUFFIX}`;
  }
  return formatMoney(String(numeric), currencyCode);
}

/**
 * A money INPUT's label, naming the currency the figure is entered in — never a bare "($)",
 * which reads as USD to some and AUD to others (PR #3341). Same three states as
 * `formatOrgMoney`: a known code, a currency the organisation has not set (said so), and one
 * not read yet (the plain label, claiming nothing).
 */
export function moneyFieldLabel(
  label: string,
  currencyCode: string | null | undefined,
  currencyResolved: boolean,
): string {
  if (currencyCode) return `${label} (${currencyCode})`;
  if (!currencyResolved) return label;
  return `${label} ${CURRENCY_NOT_SET_SUFFIX}`;
}

export function formatMoney(amount: string | null | undefined, currencyCode: string): string {
  if (amount == null || amount === '') return '';
  const numeric = Number.parseFloat(amount);
  if (!Number.isFinite(numeric)) return '';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(numeric);
  } catch {
    return `${currencyCode} ${numeric.toFixed(2)}`;
  }
}
